// Raw little-endian arrays as the sim contracts write them: no file headers, C order, the element
// type named by the descriptor's NumPy dtype (`<f4`, `|u1`) or the file extension (`.f32`, `.u8`).
// Everything numeric reaches the app as a typed array; f16 and quantized u8 decode to Float32Array.

export type DType = 'f16' | 'f32' | 'f64' | 'i8' | 'u8' | 'i16' | 'u16' | 'i32' | 'u32';
export type Typed = Float32Array | Float64Array | Int8Array | Uint8Array | Int16Array | Uint16Array | Int32Array | Uint32Array;

export const ITEM_BYTES: Record<DType, number> = { f16: 2, f32: 4, f64: 8, i8: 1, u8: 1, i16: 2, u16: 2, i32: 4, u32: 4 };

const ALIASES: Record<string, DType> = {
  f2: 'f16', f16: 'f16', float16: 'f16', half: 'f16',
  f4: 'f32', f32: 'f32', float32: 'f32', float: 'f32',
  f8: 'f64', f64: 'f64', float64: 'f64', double: 'f64',
  i1: 'i8', i8: 'i8', int8: 'i8',
  u1: 'u8', u8: 'u8', uint8: 'u8', b1: 'u8', bool: 'u8',
  i2: 'i16', i16: 'i16', int16: 'i16',
  u2: 'u16', u16: 'u16', uint16: 'u16',
  i4: 'i32', i32: 'i32', int32: 'i32',
  u4: 'u32', u32: 'u32', uint32: 'u32',
};

/** A dtype as the contracts spell it: NumPy (`<f4`, `|u1`, `u1`), short (`f32`, `u8`) or long (`float32`). */
export function parseDType(s: string): DType {
  const t = s.trim().toLowerCase();
  if (t.startsWith('>')) throw new Error(`big-endian dtype ${s}: the sim contracts are little-endian`);
  const d = ALIASES[t.replace(/^[<|=]/, '')];
  if (!d) throw new Error(`unknown dtype ${s}`);
  return d;
}

/** The dtype a file's extension names (cells-sim-v0: `.u8`, `.u32`, `.f32`…), or null. */
export function dtypeOfPath(p: string): DType | null {
  const ext = /\.([a-z0-9]+)$/i.exec(p)?.[1]?.toLowerCase();
  return ext ? (ALIASES[ext] ?? null) : null;
}

const product = (shape: readonly number[]) => shape.reduce((a, b) => a * b, 1);

let f16Table: Float32Array | null = null;
/** IEEE 754 half → single, through a 64k table built on first use. */
export function f16ToF32(src: Uint16Array, dst = new Float32Array(src.length)): Float32Array {
  if (!f16Table) {
    f16Table = new Float32Array(65536);
    for (let h = 0; h < 65536; h++) {
      const sign = h & 0x8000 ? -1 : 1;
      const exp = (h >> 10) & 0x1f;
      const frac = h & 0x3ff;
      f16Table[h] = exp === 0 ? sign * frac * 2 ** -24 : exp === 31 ? (frac ? NaN : sign * Infinity) : sign * (1 + frac / 1024) * 2 ** (exp - 15);
    }
  }
  for (let i = 0; i < src.length; i++) dst[i] = f16Table[src[i]!]!;
  return dst;
}

/** Quantized bytes → values: `offset + scale·q` (cells-sim-v0 `decode`, hemo u8 volumes). */
export function decodeU8(src: Uint8Array, offset: number, scale: number, dst = new Float32Array(src.length)): Float32Array {
  for (let i = 0; i < src.length; i++) dst[i] = offset + scale * src[i]!;
  return dst;
}

export interface DecodeOptions {
  /** Element count the descriptor's shape implies; a mismatch is an error, never a silent crop. */
  count?: number;
  /** u8 dequantization: value = offset + scale·q. */
  offset?: number;
  scale?: number;
}

/** View or decode `buf` as `dtype`. f16 and dequantized u8 come back as Float32Array; the rest are views. */
export function decode(buf: ArrayBuffer, dtype: DType, opts: DecodeOptions = {}): Typed {
  const size = ITEM_BYTES[dtype];
  if (buf.byteLength % size) throw new Error(`${buf.byteLength} bytes is not a whole number of ${dtype} elements`);
  const n = buf.byteLength / size;
  if (opts.count !== undefined && opts.count !== n) throw new Error(`expected ${opts.count} ${dtype} elements (${opts.count * size} bytes), got ${n} (${buf.byteLength} bytes)`);
  switch (dtype) {
    case 'f16':
      return f16ToF32(new Uint16Array(buf));
    case 'f32':
      return new Float32Array(buf);
    case 'f64':
      return new Float64Array(buf);
    case 'i8':
      return new Int8Array(buf);
    case 'u8':
      return opts.scale !== undefined || opts.offset !== undefined ? decodeU8(new Uint8Array(buf), opts.offset ?? 0, opts.scale ?? 1) : new Uint8Array(buf);
    case 'i16':
      return new Int16Array(buf);
    case 'u16':
      return new Uint16Array(buf);
    case 'i32':
      return new Int32Array(buf);
    case 'u32':
      return new Uint32Array(buf);
  }
}

/** A contract array descriptor: path, NumPy dtype, shape, optional byte length. */
export interface ArrayDescriptor {
  path: string;
  dtype: string;
  shape: number[];
  order?: string;
  byte_length?: number;
  units?: string;
  provenance?: string;
  timebase?: string;
  sha256?: string;
}

/** Bytes a descriptor promises, checked against `byte_length` when the writer recorded one. */
export function descriptorBytes(d: ArrayDescriptor): number {
  if (d.order && d.order.toUpperCase() !== 'C') throw new Error(`${d.path}: order ${d.order}; the contracts write C order`);
  const bytes = product(d.shape) * ITEM_BYTES[parseDType(d.dtype)];
  if (d.byte_length !== undefined && d.byte_length !== bytes) throw new Error(`${d.path}: shape ${JSON.stringify(d.shape)} × ${d.dtype} is ${bytes} bytes but byte_length says ${d.byte_length}`);
  return bytes;
}

export const elementCount = product;

// The contracts are little-endian and so is every platform the app targets; refuse to misread on one that isn't.
if (new Uint8Array(new Uint16Array([1]).buffer)[0] !== 1) throw new Error('big-endian platform: sim arrays would be misread');
